/**
 * audioEngine.js
 * ──────────────────────────────────────────────────────────────────────────
 * 100% synthesized audio via Web Audio API — no external files required.
 *
 * All sounds are tuned to the Bhupali (pentatonic) scale: Sa Re Ga Pa Dha
 * so every UI sound feels like it belongs in the same musical world as the app.
 *
 * Exported functions:
 *   initAudio()            — call once on first user gesture
 *   playCorrect()          — bright ascending chime (note sung well)
 *   playWrong()            — soft descending wobble (missed note)
 *   playStarEarned(n)      — ascending arpeggio, depth = star count
 *   playLevelComplete()    — Santoor hammer-stroke Sa→Ga→Pa arpeggio (level end)
 *   playSantoorResults()   — Santoor hammer-stroke Pa→Ga→Sa descent (results screen)
 *   playLevelUnlock()      — rising bell sparkle
 *   playButtonTap()        — micro click for UI buttons
 *   playCalibrationDone()  — warm bell  + success bloom
 *   startTanpuraDrone(saHz) — start looping tanpura-style drone (returns stop fn)
 *   setMasterVolume(0-1)   — adjust global volume
 */

import { Capacitor } from '@capacitor/core';

// ── Singleton AudioContext ────────────────────────────────────────────────────
let _ctx = null;
let _master = null;
let _sfxVol = 0.38;   // reduced — prevents jarring peaks on iOS at high system volume
let _musicVol = 0.25; // reduced — background music / tanpura

function ctx() {
  if (!_ctx || _ctx.state === 'closed') {
    _ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
    _master = _ctx.createGain();
    _master.gain.value = 1.0;
    _master.connect(_ctx.destination);
  }
  if (_ctx.state === 'suspended') _ctx.resume();
  return _ctx;
}

// ── Init (call on first user gesture to unlock iOS AudioContext) ──────────────
export function initAudio() {
  try { ctx(); } catch (_) {}
}

export function setMasterVolume(v) {
  if (_master) _master.gain.value = Math.max(0, Math.min(1, v));
}

// ── Low-level helpers ─────────────────────────────────────────────────────────

/** Create an oscillator + envelope, returns a "play and forget" trigger */
function playNote(freq, duration, {
  type = 'sine',
  attack = 0.01,
  decay = 0.1,
  sustain = 0.6,
  release = 0.25,
  volume = _sfxVol,
  startTime = 0,
  detune = 0,
  filterFreq = null,
} = {}) {
  const c = ctx();
  const t = c.currentTime + startTime;

  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  if (detune) osc.detune.value = detune;

  const env = c.createGain();
  env.gain.setValueAtTime(0, t);
  env.gain.linearRampToValueAtTime(volume, t + attack);
  env.gain.linearRampToValueAtTime(volume * sustain, t + attack + decay);
  env.gain.setValueAtTime(volume * sustain, t + duration - release);
  env.gain.linearRampToValueAtTime(0, t + duration);

  let chain = env;

  if (filterFreq) {
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = filterFreq;
    env.connect(lp);
    lp.connect(_master);
  } else {
    env.connect(_master);
  }

  osc.connect(env);
  osc.start(t);
  osc.stop(t + duration);
}

/** Bell-like tone: mix of sine + partial harmonics with quick decay */
function playBell(freq, duration, { volume = _sfxVol, startTime = 0 } = {}) {
  const c = ctx();
  const t = c.currentTime + startTime;

  [1, 2, 3, 4.2].forEach((mult, i) => {
    const osc = c.createOscillator();
    osc.type = i === 0 ? 'sine' : 'sine';
    osc.frequency.value = freq * mult;

    const env = c.createGain();
    const v = volume * [0.7, 0.25, 0.1, 0.06][i];
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(v, t + 0.005);
    env.gain.exponentialRampToValueAtTime(v * 0.001, t + duration);

    osc.connect(env);
    env.connect(_master);
    osc.start(t);
    osc.stop(t + duration);
  });
}

// ── Bhupali scale ratios (Sa Re Ga Pa Dha) relative to Sa ────────────────────
const BHUPALI = [1, 9/8, 5/4, 3/2, 5/3]; // Sa Re Ga Pa Dha

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Bright rising chime — "correct note sung"
 * Two-note ascending interval (Sa→Ga)
 */
export function playCorrect() {
  try {
    const sa = 523.25; // C5
    playBell(sa,        0.5, { volume: _sfxVol * 0.9 });
    playBell(sa * 5/4,  0.7, { volume: _sfxVol, startTime: 0.07 });
  } catch (_) {}
}

/**
 * Soft descending wobble — "note missed"
 * Slightly detuned descending pair, muted
 */
export function playWrong() {
  try {
    const c = ctx();
    const t = c.currentTime;
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(340, t);
    osc.frequency.linearRampToValueAtTime(260, t + 0.25);

    const env = c.createGain();
    env.gain.setValueAtTime(_sfxVol * 0.45, t);
    env.gain.linearRampToValueAtTime(0, t + 0.3);

    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 800;

    osc.connect(env);
    env.connect(lp);
    lp.connect(_master);
    osc.start(t);
    osc.stop(t + 0.35);
  } catch (_) {}
}

/**
 * Star-earned arpeggio — plays n notes of the Bhupali scale ascending
 * 1 star → Sa only, 5 stars → full Sa Re Ga Pa Dha phrase
 */
export function playStarEarned(stars = 3) {
  try {
    const sa = 523.25;
    const count = Math.max(1, Math.min(5, stars));
    const gap = 0.1;
    for (let i = 0; i < count; i++) {
      playBell(sa * BHUPALI[i], 0.55 + i * 0.05, {
        volume: _sfxVol * (0.7 + i * 0.06),
        startTime: i * gap,
      });
    }
  } catch (_) {}
}

// ── Santoor (hammered dulcimer) stroke helper ───────────────────────────────
/**
 * Simulate one santoor hammer-strike.
 * The santoor's signature: two strings per note tuned ±5 cents apart give
 * a natural chorus/beating effect; very fast attack + exponential decay tail.
 */
function _playSantoorStroke(freq, startTime) {
  const c = ctx();
  const t = c.currentTime + startTime;
  const vol = _sfxVol * 0.72;

  // Two slightly detuned strings — the defining santoor characteristic
  [-5, 5].forEach((detuneCents) => {
    [
      { mult: 1,   volScale: 0.85 },  // fundamental
      { mult: 2,   volScale: 0.28 },  // octave partial
      { mult: 3,   volScale: 0.10 },  // fifth partial
    ].forEach(({ mult, volScale }) => {
      const osc = c.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq * mult;
      osc.detune.value = detuneCents;

      const env = c.createGain();
      const peak = vol * volScale;
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(peak, t + 0.003);         // very fast strike
      env.gain.exponentialRampToValueAtTime(peak * 0.30, t + 0.08);  // initial snap
      env.gain.exponentialRampToValueAtTime(peak * 0.001, t + 0.85); // long ring

      // Light high-pass — adds that metallic struck-string clarity
      const hp = c.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 160;

      osc.connect(env);
      env.connect(hp);
      hp.connect(_master);
      osc.start(t);
      osc.stop(t + 0.9);
    });
  });
}

/**
 * Level complete — Santoor: Sa→Ga→Pa ascending arpeggio (~1s)
 * Three hammer-strokes spaced 0.28s apart; warm, instant, unmistakably Indian.
 */
export function playLevelComplete() {
  try {
    const sa = 392; // G4 — concert pitch, warm santoor register
    [
      { freq: sa,         startTime: 0    },  // Sa
      { freq: sa * 5 / 4, startTime: 0.28 },  // Ga
      { freq: sa * 3 / 2, startTime: 0.56 },  // Pa
    ].forEach(({ freq, startTime }) => {
      _playSantoorStroke(freq, startTime);
    });
  } catch (_) {}
}

/**
 * Results screen — Santoor: Pa→Ga→Sa descending resolution (~1s)
 * Mirror phrase of playLevelComplete — ascending triggers it, descending
 * settles the user into the results view.
 */
export function playSantoorResults() {
  try {
    const sa = 392; // G4 — same register as level-complete for coherence
    [
      { freq: sa * 3 / 2, startTime: 0    },  // Pa
      { freq: sa * 5 / 4, startTime: 0.28 },  // Ga
      { freq: sa,         startTime: 0.56 },  // Sa
    ].forEach(({ freq, startTime }) => {
      _playSantoorStroke(freq, startTime);
    });
  } catch (_) {}
}

/**
 * Level unlock sparkle — fast rising arpeggio with shimmer
 */
export function playLevelUnlock() {
  try {
    const sa = 392; // G4 — sparkly register
    [0, 2, 4, 4].forEach((deg, i) => {
      const freq = deg === 4 && i === 3 ? sa * BHUPALI[4] * 2 : sa * BHUPALI[deg];
      playBell(freq, 0.45, {
        volume: _sfxVol * 0.7,
        startTime: i * 0.08,
      });
    });
  } catch (_) {}
}

/**
 * Micro tap — very short soft click for UI button presses
 */
export function playButtonTap() {
  try {
    const c = ctx();
    const t = c.currentTime;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * 0.04), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (data.length * 0.15));
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const env = c.createGain();
    env.gain.value = _sfxVol * 0.25;
    const lp = c.createBiquadFilter();
    lp.type = 'bandpass';
    lp.frequency.value = 1200;
    lp.Q.value = 2;
    src.connect(lp);
    lp.connect(env);
    env.connect(_master);
    src.start(t);
  } catch (_) {}
}

/**
 * Calibration complete — warm bell bloom signalling "we found your Sa"
 */
export function playCalibrationDone() {
  try {
    const sa = 440; // A4 — warm, centered
    // Three notes: Sa Pa Sa (open fifth feel)
    [[sa, 0], [sa * 3/2, 0.12], [sa * 2, 0.28]].forEach(([freq, st]) => {
      playBell(freq, 0.9, { volume: _sfxVol * 0.8, startTime: st });
    });
  } catch (_) {}
}

/**
 * Onboarding slide change — subtle soft chime
 */
export function playSlideChime() {
  try {
    playBell(659.25, 0.4, { volume: _sfxVol * 0.45 }); // E5
  } catch (_) {}
}

// ── Tanpura Drone Synthesizer ─────────────────────────────────────────────────
/**
 * Synthesize a looping tanpura-style drone pitched to the user's Sa.
 *
 * Tanpura strings: Sa (×2), Pa, low Sa
 * Each string is plucked on a staggered cycle with an amplitude envelope.
 *
 * @param {number} saHz  — fundamental Sa frequency in Hz (from calibration)
 * @param {number} [volume=0.3]
 * @returns {function} stop — call to fade out and stop the drone
 */
export function startTanpuraDrone(saHz = 261.63, volume = _musicVol) {
  try {
    const c = ctx();

    // The four tanpura strings (intervals relative to Sa)
    const strings = [
      { freq: saHz * 2,   label: 'sa_high'  },  // Sa (high)
      { freq: saHz * 3/2, label: 'pa'       },  // Pa (fifth)
      { freq: saHz,       label: 'sa_mid'   },  // Sa (mid)
      { freq: saHz / 2,   label: 'sa_low'   },  // Sa (low)
    ];

    const masterGain = c.createGain();
    masterGain.gain.value = volume;
    masterGain.connect(_master);

    // Warm, slightly detuned oscillators per string
    const nodes = strings.map(({ freq }, i) => {
      const osc = c.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      // Slight detune per string for beating / warmth
      osc.detune.value = [-4, 3, -2, 1][i];

      // Pluck envelope: fast attack, medium decay, slow sustain tail
      const env = c.createGain();
      env.gain.value = 0;

      // Low-pass filter — tanpura has a warm tone, not bright
      const lp = c.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = freq * 6;
      lp.Q.value = 0.8;

      osc.connect(lp);
      lp.connect(env);
      env.connect(masterGain);

      osc.start();
      return { osc, env };
    });

    // Pluck scheduler — each string plucked in sequence
    const CYCLE_TIME = 2.4;     // seconds per full cycle
    const pluckInterval = CYCLE_TIME / strings.length;
    let stopped = false;
    let startT = c.currentTime + 0.1;

    function schedulePlucks(fromTime) {
      if (stopped) return;
      strings.forEach((_, i) => {
        const t = fromTime + i * pluckInterval;
        const env = nodes[i].env.gain;
        const pluckVol = [0.7, 0.5, 0.65, 0.4][i];
        env.setValueAtTime(0, t);
        env.linearRampToValueAtTime(pluckVol, t + 0.02);
        env.exponentialRampToValueAtTime(0.001, t + pluckInterval * 0.9);
      });

      // Schedule next cycle
      if (!stopped) {
        const nextCycle = fromTime + CYCLE_TIME;
        const delay = (nextCycle - c.currentTime) * 1000 - 50;
        setTimeout(() => schedulePlucks(nextCycle), Math.max(0, delay));
      }
    }

    schedulePlucks(startT);

    // Return stop function
    return function stop() {
      stopped = true;
      const t = c.currentTime;
      masterGain.gain.setValueAtTime(masterGain.gain.value, t);
      masterGain.gain.linearRampToValueAtTime(0, t + 1.5);
      setTimeout(() => {
        nodes.forEach(({ osc }) => { try { osc.stop(); } catch (_) {} });
        masterGain.disconnect();
      }, 1800);
    };
  } catch (_) {
    return () => {}; // safe no-op
  }
}

// ── Ambient music player (for external mp3) ───────────────────────────────────
/**
 * Plays a looping ambient background track from a URL.
 * Uses HTML5 Audio (not Web Audio API) so it doesn't fight with the mic stream.
 *
 * @param {string} url    — path to the mp3 (e.g. '/sounds/intro.mp3')
 * @param {number} volume — 0-1, default 0.3
 * @returns {{ stop: function, setVolume: function, fadeOut: function }}
 */
export function playAmbientTrack(url, volume = 0.3) {
  const audio = new Audio(url);
  audio.loop = true;
  audio.volume = 0;
  audio.preload = 'auto';

  let _stopped = false;

  audio.play().catch(() => {}); // may fail until user gesture — that's fine

  // Fade in
  const fadeIn = setInterval(() => {
    if (_stopped) { clearInterval(fadeIn); return; }
    if (audio.volume < volume - 0.02) {
      audio.volume = Math.min(volume, audio.volume + 0.02);
    } else {
      audio.volume = volume;
      clearInterval(fadeIn);
    }
  }, 80);

  return {
    stop(fadeDuration = 1200) {
      _stopped = true;
      clearInterval(fadeIn);
      if (fadeDuration === 0) {
        audio.volume = 0;
        audio.pause();
        audio.src = '';
      } else {
        this.fadeOut(fadeDuration).then(() => {
          audio.pause();
          audio.src = '';
        });
      }
    },
    setVolume(v) {
      volume = Math.max(0, Math.min(1, v));
      if (!_stopped) audio.volume = volume;
    },
    fadeOut(duration = 1200) {
      return new Promise((resolve) => {
        const step = audio.volume / (duration / 80);
        const interval = setInterval(() => {
          if (audio.volume > step) {
            audio.volume = Math.max(0, audio.volume - step);
          } else {
            audio.volume = 0;
            clearInterval(interval);
            resolve();
          }
        }, 80);
      });
    },
  };
}
